import { useColorModeValue, LinkBox, Card, Flex, Box, LinkOverlay } from "@chakra-ui/react";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import ActionSummaryStatusTag from "components/Tag/ActionSummaryStatusTag";
import CDS, { ActionTypeTag } from "design-system";
import ImmediateTag from "components/Tag/ImmediateTag";
import { Text12 } from "components/Text";
import { hasPendingIssues } from "features/Issues/helpers";
import { FullMeetingStatus } from "features/Resources/Meeting/Components/MeetingStatus";
import { Meeting } from "features/Resources/Meeting/models";
import { truncateEnd, pluralize } from "helpers/string";
import { isImmediate } from "../../helpers";
import normalizeSendAt from "../../helpers/normalizeSendAt";
import { ActionSummary } from "../../models";
import MiniResourceDotMenu from "../Button/MiniResourceDotMenu";
import { DisplayRecipientsValue } from "../DisplayRecipients";
import PrimarySendAtValue from "../PrimarySendAtValue";

interface StageActionCardProps {
  action: ActionSummary;
  handleClick: () => void;
  isPending: boolean;
}

export default function StageActionCard({ action, isPending, handleClick }: StageActionCardProps) {
  const headerTextColor = useColorModeValue("cdsGrey.900", "var(--chakra-colors-whiteAlpha-900)");
  const showIssues = hasPendingIssues(action, isPending);

  if (!action) {
    console.error("Action is undefined");
    return null;
  }

  const normalizedSendAt = normalizeSendAt(action);
  const isMeeting = action.actionType === "meeting";
  const issueCount = action.action?.issueIds?.length ?? 0;

  return (
    <LinkBox as={Card} width="250px" height="auto">
      <Flex py="4" pl="4" pr="3" direction="column" justify="space-between" h="100%">
        <Flex direction="column">
          <Flex direction="column" gap="1">
            <Flex align="center" justify="space-between" w="100%">
              <ActionTypeTag.IconBadgeWithMeetingType actionType={action.actionType} meetingType={isMeeting ? (action.action as Meeting).meetingType : undefined} />
              <Flex position="relative" zIndex={1} gap="2">
                <MiniResourceDotMenu action={action.action} />
              </Flex>
            </Flex>
            <Box w="100%">
              <CDS.Text12 fontWeight="bold" color={headerTextColor} textAlign="left">
                <LinkOverlay
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClick();
                  }}
                >
                  {truncateEnd(action.action?.name, 38)}
                </LinkOverlay>
              </CDS.Text12>
            </Box>
          </Flex>

          <Flex direction="column" mt="2" gap="2">
            <HorizontalGreyBlackField size="xs" label={action.actionType === "checkpoint" ? "Check" : "To"}>
              <Text12>
                <DisplayRecipientsValue action={action} />
              </Text12>
            </HorizontalGreyBlackField>

            <HorizontalGreyBlackField size="xs" label={normalizedSendAt.label}>
              {isPending && isImmediate(action) ? <ImmediateTag /> : <PrimarySendAtValue action={action} size="sm" />}
            </HorizontalGreyBlackField>
          </Flex>
        </Flex>

        <Flex width="fit-content">
          {isMeeting ? (
            <FullMeetingStatus meeting={action.action as Meeting} mt="2" />
          ) : (
            <ActionSummaryStatusTag action={action} mt="2" />
          )}
        </Flex>

        {showIssues && (
          <Flex width="100%" mt="2">
            <Text12 color="error.DEFAULT">
              {issueCount} {pluralize("Issue", issueCount)}
            </Text12>
          </Flex>
        )}
      </Flex>
    </LinkBox>
  );
}
